import { fitDiagramToBounds, getDiagramState } from './helpers';
import { isDiagramFlowPlaying, replayDiagramFlow, toggleDiagramFlowPause, } from './flow';
var TOOLBAR_KEY = 'diagramToolbar';
var STYLE_ID = 'lightdraw-diagram-toolbar-style';
var ZOOM_MIN = 0.35;
var ZOOM_MAX = 2.75;
var ZOOM_STEP = 1.18;
function ensureToolbarStyles() {
    if (typeof document === 'undefined')
        return;
    if (document.getElementById(STYLE_ID))
        return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = "\n.ld-diagram-toolbar{\n  position:absolute;\n  top:10px;\n  right:10px;\n  z-index:20;\n  display:flex;\n  align-items:center;\n  gap:4px;\n  padding:4px;\n  border-radius:10px;\n  background:rgba(15,23,42,.82);\n  border:1px solid rgba(148,163,184,.35);\n  box-shadow:0 8px 24px rgba(0,0,0,.28);\n  backdrop-filter:blur(8px);\n  pointer-events:auto;\n  font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;\n  user-select:none;\n}\n.ld-diagram-toolbar button{\n  appearance:none;\n  border:0;\n  margin:0;\n  min-width:32px;\n  height:30px;\n  padding:0 8px;\n  border-radius:7px;\n  background:transparent;\n  color:#e2e8f0;\n  font-size:14px;\n  font-weight:600;\n  line-height:1;\n  cursor:pointer;\n}\n.ld-diagram-toolbar button:hover{\n  background:rgba(148,163,184,.22);\n}\n.ld-diagram-toolbar button:focus-visible{\n  outline:2px solid #38bdf8;\n  outline-offset:1px;\n}\n.ld-diagram-toolbar button.ld-fit{\n  font-size:12px;\n  letter-spacing:.02em;\n}\n.ld-diagram-toolbar .ld-zoom-label{\n  min-width:40px;\n  text-align:center;\n  color:#94a3b8;\n  font-size:12px;\n  font-variant-numeric:tabular-nums;\n  pointer-events:none;\n}\n.ld-diagram-toolbar[hidden]{display:none!important}\n";
    document.head.appendChild(style);
}
function resolveHost(app, mount) {
    var _a, _b, _c;
    if (mount)
        return mount;
    if (typeof app.getContainer === 'function') {
        return app.getContainer();
    }
    var surface = (_b = (_a = app.getRenderer()) === null || _a === void 0 ? void 0 : _a.getElement) === null || _b === void 0 ? void 0 : _b.call(_a);
    return (_c = surface === null || surface === void 0 ? void 0 : surface.parentElement) !== null && _c !== void 0 ? _c : null;
}
function clampZoom(z) {
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
}
function isHtmlSurface(app) {
    var _a, _b, _c;
    var el = (_b = (_a = app.getRenderer()) === null || _a === void 0 ? void 0 : _a.getElement) === null || _b === void 0 ? void 0 : _b.call(_a);
    return !!(el && ((_c = el.classList) === null || _c === void 0 ? void 0 : _c.contains('lightdraw-html-root')));
}
function applyViewZoom(app, zoom) {
    var _a, _b;
    var z = clampZoom(zoom);
    app.camera.setZoom(z);
    if (isHtmlSurface(app)) {
        var el = app.getRenderer().getElement();
        el.style.transformOrigin = '0 0';
        el.style.transform = z === 1 ? '' : "scale(".concat(z, ")");
    }
    (_a = app.requestRender) === null || _a === void 0 ? void 0 : _a.call(app);
    (_b = app.render) === null || _b === void 0 ? void 0 : _b.call(app);
}
function readZoom(app) {
    return clampZoom(app.camera.zoom || 1);
}
/** Normalize `flow.chrome` into concrete flags. */
export function resolveDiagramChrome(chrome, flowEnabled) {
    if (chrome === false)
        return { enabled: false, flow: false, zoom: false };
    if (chrome === true || chrome === undefined) {
        // Default: show toolbar when flow is applied/enabled
        return { enabled: flowEnabled, flow: flowEnabled, zoom: flowEnabled };
    }
    var flow = chrome.flow !== false && flowEnabled;
    var zoom = chrome.zoom !== false;
    var enabled = flow || zoom;
    return { enabled: enabled, flow: flow, zoom: zoom };
}
function btn(label, title, className) {
    if (className === void 0) { className = ''; }
    var b = document.createElement('button');
    b.type = 'button';
    b.textContent = label;
    b.title = title;
    b.setAttribute('aria-label', title);
    if (className)
        b.className = className;
    return b;
}
/**
 * Install (or replace) the built-in diagram toolbar on the App host.
 */
export function installDiagramToolbar(app, root, options) {
    if (options === void 0) { options = {}; }
    uninstallDiagramToolbar(root);
    ensureToolbarStyles();
    var showFlow = options.flow === true;
    var showZoom = options.zoom !== false;
    var host = resolveHost(app, options.mount);
    if (!host) {
        return {
            root: root,
            el: document.createElement('div'),
            opts: { flow: showFlow, zoom: showZoom },
            sync: function () { return undefined; },
            destroy: function () { return undefined; },
        };
    }
    var cs = typeof getComputedStyle === 'function' ? getComputedStyle(host) : null;
    if (cs && cs.position === 'static') {
        host.style.position = 'relative';
    }
    var el = document.createElement('div');
    el.className = 'ld-diagram-toolbar';
    el.setAttribute('role', 'toolbar');
    el.setAttribute('aria-label', 'Diagram controls');
    var playBtn = null;
    var replayBtn = null;
    var zoomLabel = null;
    if (showFlow) {
        playBtn = btn('⏸', 'Pause flow');
        replayBtn = btn('↻', 'Replay flow');
        el.appendChild(playBtn);
        el.appendChild(replayBtn);
    }
    if (showZoom) {
        var zoomOut = btn('−', 'Zoom out');
        zoomLabel = document.createElement('span');
        zoomLabel.className = 'ld-zoom-label';
        zoomLabel.textContent = '100%';
        var zoomIn = btn('+', 'Zoom in');
        var fit = btn('Fit', 'Fit to view', 'ld-fit');
        el.appendChild(zoomOut);
        el.appendChild(zoomLabel);
        el.appendChild(zoomIn);
        el.appendChild(fit);
        zoomOut.addEventListener('click', function (e) {
            e.stopPropagation();
            applyViewZoom(app, readZoom(app) / ZOOM_STEP);
            sync();
        });
        zoomIn.addEventListener('click', function (e) {
            e.stopPropagation();
            applyViewZoom(app, readZoom(app) * ZOOM_STEP);
            sync();
        });
        fit.addEventListener('click', function (e) {
            var _a, _b;
            e.stopPropagation();
            applyViewZoom(app, 1);
            app.camera.setPosition(0, 0);
            var _c = app.getSize(), width = _c.width, height = _c.height;
            fitDiagramToBounds(root, width, height, 24);
            (_a = app.requestRender) === null || _a === void 0 ? void 0 : _a.call(app);
            (_b = app.render) === null || _b === void 0 ? void 0 : _b.call(app);
            sync();
        });
    }
    var sync = function () {
        var flow = getDiagramState(root).flow;
        var flowOn = !!flow && flow.enabled !== false;
        if (playBtn) {
            playBtn.hidden = !flowOn;
            var playing = isDiagramFlowPlaying(root);
            playBtn.textContent = playing ? '⏸' : '▶';
            playBtn.title = playing ? 'Pause flow' : 'Play flow';
            playBtn.setAttribute('aria-label', playBtn.title);
        }
        if (replayBtn) {
            replayBtn.hidden = !flowOn;
        }
        if (zoomLabel) {
            zoomLabel.textContent = "".concat(Math.round(readZoom(app) * 100), "%");
        }
        var anyVisible = showZoom ||
            Array.from(el.querySelectorAll('button')).some(function (b) { return !b.hidden; });
        el.hidden = !anyVisible;
    };
    if (playBtn) {
        playBtn.addEventListener('click', function (e) {
            e.stopPropagation();
            toggleDiagramFlowPause(app, root);
            sync();
        });
    }
    if (replayBtn) {
        replayBtn.addEventListener('click', function (e) {
            e.stopPropagation();
            replayDiagramFlow(app, root);
            sync();
        });
    }
    host.appendChild(el);
    var poll = window.setInterval(function () {
        if (!el.isConnected) {
            window.clearInterval(poll);
            return;
        }
        sync();
    }, 400);
    var handle = {
        root: root,
        el: el,
        opts: { flow: showFlow, zoom: showZoom },
        sync: sync,
        destroy: function () {
            var _a;
            window.clearInterval(poll);
            el.remove();
            if (((_a = root.metadata) === null || _a === void 0 ? void 0 : _a[TOOLBAR_KEY]) === handle) {
                delete root.metadata[TOOLBAR_KEY];
            }
        },
    };
    root.metadata[TOOLBAR_KEY] = handle;
    sync();
    return handle;
}
/** Remove toolbar DOM + metadata for a diagram root. */
export function uninstallDiagramToolbar(root) {
    var _a;
    var handle = (_a = root.metadata) === null || _a === void 0 ? void 0 : _a[TOOLBAR_KEY];
    if (handle) {
        handle.destroy();
    }
}
/**
 * Keep toolbar in sync with `diagramState.flow.chrome`.
 * Called from apply / pause / resume / stop.
 */
export function syncDiagramToolbar(app, root) {
    var _a;
    var flow = getDiagramState(root).flow;
    var flowEnabled = !!flow && flow.enabled !== false;
    var flags = resolveDiagramChrome(flow === null || flow === void 0 ? void 0 : flow.chrome, flowEnabled);
    if (!flags.enabled) {
        uninstallDiagramToolbar(root);
        return;
    }
    var existing = (_a = root.metadata) === null || _a === void 0 ? void 0 : _a[TOOLBAR_KEY];
    if ((existing === null || existing === void 0 ? void 0 : existing.el.isConnected) &&
        existing.opts.flow === flags.flow &&
        existing.opts.zoom === flags.zoom) {
        existing.sync();
        return;
    }
    installDiagramToolbar(app, root, { flow: flags.flow, zoom: flags.zoom });
}
